import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DocumentsPanel } from '@/components/staff/DocumentsPanel';
import { StaffTasksAdmin } from '@/components/staff/StaffTasksAdmin';
import { JobRolesField } from '@/components/staff/JobRolesField';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff, KeyRound, ShieldCheck, ChevronDown, ChevronUp, Pencil, Power } from 'lucide-react';

const STATUS_BADGE = 'text-xs font-semibold px-2.5 py-0.5 rounded-full';
const ACTIVITY_PAGE_SIZE = 50;

const ACTION_LABELS = {
  STAFF_PIN_RESET: 'PIN reset',
  STAFF_PIN_VIEWED: 'PIN viewed',
  STAFF_CREATED: 'Account created',
  REDEMPTION: 'Tiffin redemption',
  SUBSCRIPTION_ACTIVATED: 'Activated subscription',
};

export default function StaffProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [staff, setStaff] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityPage, setActivityPage] = useState(1);
  const [activityOpen, setActivityOpen] = useState(false);
  const [pin, setPin] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', role: 'COUNTER', phone: '', positionIds: [] });
  const [roles, setRoles] = useState([]);
  const [editBusy, setEditBusy] = useState(false);
  const [activeBusy, setActiveBusy] = useState(false);

  const load = () =>
    staffApi.get(`/staff/${id}`, { params: { page: activityPage, pageSize: ACTIVITY_PAGE_SIZE } }).then(({ data }) => {
      setStaff(data.staff);
      setActivity(data.activity);
      setActivityTotal(data.activityTotal ?? data.activity.length);
    });

  useEffect(() => {
    load().catch((err) => toast.error(formatApiError(err)));
    staffApi.get('/scheduling/positions').then(({ data }) => setRoles(data.positions)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activityPage]);

  const revealPin = async () => {
    try {
      const { data } = await staffApi.get(`/staff/${id}/pin`);
      if (!data.canReveal) return toast.error('This PIN predates secure viewing — reset it to a new PIN to view it here.');
      setPin(data.pin);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const savePin = async () => {
    if (!/^\d{4}$/.test(pinValue)) return toast.error('PIN must be exactly 4 digits');
    setPinBusy(true);
    try {
      await staffApi.patch(`/staff/${id}/pin`, { pin: pinValue });
      toast.success('POS PIN updated');
      setPin(null);
      setPinOpen(false);
      setPinValue('');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPinBusy(false);
    }
  };

  const openEdit = () => {
    setForm({ name: staff.name, email: staff.email, role: staff.role, phone: staff.phone || '', positionIds: (staff.jobRoles || []).map((r) => r.id) });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error('Name and email are required');
    setEditBusy(true);
    try {
      await staffApi.patch(`/staff/${id}`, {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        phone: form.phone.trim(),
        positionIds: form.positionIds,
      });
      toast.success('Staff details updated');
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditBusy(false);
    }
  };

  const toggleActive = async () => {
    setActiveBusy(true);
    try {
      await staffApi.patch(`/staff/${id}`, { isActive: !staff.isActive });
      toast.success(staff.isActive ? 'Staff set to Inactive' : 'Staff set to Active');
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActiveBusy(false);
    }
  };

  if (!staff) return <p className="text-muted-ink text-sm">Loading staff…</p>;

  return (
    <div className="space-y-6">
      <button data-testid="staff-profile-back-btn" onClick={() => navigate('/admin/staff')} className="flex items-center gap-2 text-sm text-muted-ink hover:text-charcoal">
        <ArrowLeft size={16} /> Back to Staff
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-saffron/10 text-saffron flex items-center justify-center font-heading text-xl font-700">
            {staff.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-700 text-charcoal" data-testid="staff-profile-name">{staff.name}</h1>
            <p className="text-sm text-muted-ink">{staff.email} · {staff.role}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`${STATUS_BADGE} ${staff.isActive ? 'bg-coriander/10 text-coriander' : 'bg-red-100 text-red-700'}`}>
            {staff.isActive ? 'Active' : 'Inactive'}
          </span>
          <span className={`${STATUS_BADGE} ${staff.posEnabled ? 'bg-coriander/10 text-coriander' : 'bg-bone text-muted-ink'}`}>
            {staff.posEnabled ? 'POS enabled' : 'POS off'}
          </span>
          <Button data-testid="staff-edit-open-btn" variant="outline" size="sm" onClick={openEdit} className="gap-1.5">
            <Pencil size={14} /> Edit
          </Button>
          <Button
            data-testid="staff-active-toggle-btn"
            variant="outline"
            size="sm"
            disabled={activeBusy}
            onClick={toggleActive}
            className={`gap-1.5 ${staff.isActive ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-coriander text-coriander hover:bg-coriander/10'}`}
          >
            <Power size={14} /> {staff.isActive ? 'Set Inactive' : 'Set Active'}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Account details */}
        <div className="bg-white rounded-xl border border-border/40 p-5 space-y-3">
          <h2 className="font-heading font-700 text-charcoal flex items-center gap-2"><ShieldCheck size={16} className="text-coriander" /> Account</h2>
          <Detail label="Full name" value={staff.name} />
          <Detail label="Email" value={staff.email} />
          <Detail label="Phone" value={staff.phone || '—'} />
          <Detail label="Role" value={staff.role} />
          <div className="flex items-start justify-between text-sm gap-3">
            <span className="text-muted-ink shrink-0">Job roles</span>
            <div className="flex flex-wrap gap-1 justify-end" data-testid="staff-profile-jobroles">
              {(staff.jobRoles || []).length === 0 && <span className="text-charcoal font-medium">—</span>}
              {(staff.jobRoles || []).map((r) => (
                <span key={r.id} className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{ background: (r.color || '#E8833A') + '22', color: r.color || '#B45309' }}>{r.name}</span>
              ))}
            </div>
          </div>
          <Detail label="Joined" value={new Date(staff.createdAt).toLocaleDateString('en-CA')} />
        </div>

        {/* POS access + PIN */}
        <div className="bg-white rounded-xl border border-border/40 p-5 space-y-3">
          <h2 className="font-heading font-700 text-charcoal flex items-center gap-2"><KeyRound size={16} className="text-saffron" /> POS Access</h2>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-ink">POS PIN</span>
            <div className="flex items-center gap-3">
              {staff.hasPin ? (
                <>
                  <span data-testid="staff-profile-pin-value" className="font-mono tracking-[0.25em] text-charcoal">{pin || '••••'}</span>
                  <button
                    data-testid="staff-profile-pin-reveal-btn"
                    onClick={() => (pin ? setPin(null) : revealPin())}
                    className="text-muted-ink hover:text-charcoal"
                    title={pin ? 'Hide PIN' : 'Show PIN'}
                  >
                    {pin ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </>
              ) : (
                <span className="text-sm text-muted-ink">No PIN set</span>
              )}
            </div>
          </div>
          <Button data-testid="staff-profile-set-pin-btn" variant="outline" className="w-full" onClick={() => { setPinValue(''); setPinOpen(true); }}>
            {staff.hasPin ? 'Reset PIN' : 'Set PIN'}
          </Button>
        </div>
      </div>

      {/* Documents & HR (real uploads) + admin-assigned activities */}
      <DocumentsPanel basePath={`/staff/${id}`} canUpload canDelete />
      <StaffTasksAdmin staffId={id} />

      {/* Recent activity — minimized by default, 50 per page */}
      <div className="bg-white rounded-xl border border-border/40 p-5">
        <button
          type="button"
          data-testid="staff-activity-toggle"
          onClick={() => setActivityOpen((o) => !o)}
          className="w-full flex items-center justify-between"
        >
          <h2 className="font-heading font-700 text-charcoal">
            Recent Activity{activityTotal ? ` (${activityTotal})` : ''}
          </h2>
          {activityOpen ? <ChevronUp size={18} className="text-muted-ink" /> : <ChevronDown size={18} className="text-muted-ink" />}
        </button>

        {activityOpen && (
          <div className="mt-3">
            <div className="divide-y divide-border/30">
              {activity.map((a) => (
                <div key={a.id} className="py-2.5 flex items-center justify-between text-sm" data-testid="staff-activity-row">
                  <span className="text-charcoal">{ACTION_LABELS[a.action] || a.action}{a.entityType ? ` · ${a.entityType}` : ''}</span>
                  <span className="text-xs text-muted-ink">{new Date(a.createdAt).toLocaleString('en-CA')}</span>
                </div>
              ))}
              {activity.length === 0 && <p className="text-sm text-muted-ink py-2">No recorded activity yet.</p>}
            </div>

            {activityTotal > ACTIVITY_PAGE_SIZE && (
              <div className="flex items-center justify-between pt-3 mt-1 border-t border-border/30">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="staff-activity-prev-btn"
                  disabled={activityPage <= 1}
                  onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-xs text-muted-ink" data-testid="staff-activity-page-label">
                  Page {activityPage} of {Math.ceil(activityTotal / ACTIVITY_PAGE_SIZE)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="staff-activity-next-btn"
                  disabled={activityPage >= Math.ceil(activityTotal / ACTIVITY_PAGE_SIZE)}
                  onClick={() => setActivityPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent data-testid="staff-profile-pin-dialog">
          <DialogHeader>
            <DialogTitle>{staff.hasPin ? 'Reset' : 'Set'} POS PIN for {staff.name}</DialogTitle>
            <DialogDescription>Enter a 4-digit PIN this staff member will use at the POS.</DialogDescription>
          </DialogHeader>
          <Input
            data-testid="staff-profile-pin-input"
            value={pinValue}
            onChange={(e) => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
            inputMode="numeric"
            maxLength={4}
            placeholder="0000"
            className="text-center text-2xl tracking-[0.5em]"
          />
          <DialogFooter>
            <Button data-testid="staff-profile-pin-save-btn" disabled={pinBusy} onClick={savePin} className="w-full bg-saffron hover:bg-saffron-hover">
              {pinBusy ? 'Saving…' : 'Save PIN'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="staff-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit staff details</DialogTitle>
            <DialogDescription>Correct any mistakes in this staff member&apos;s record.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="staff-edit-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="staff-edit-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="staff-edit-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="e.g. 780-555-0000" />
            </div>
            <div className="space-y-1.5">
              <Label>Access Level</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-edit-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="MANAGER">Manager</SelectItem>
                  <SelectItem value="COUNTER">Counter Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <JobRolesField allRoles={roles} setAllRoles={setRoles} value={form.positionIds} onChange={(ids) => setForm({ ...form, positionIds: ids })} />
          </div>
          <DialogFooter>
            <Button data-testid="staff-edit-save-btn" disabled={editBusy} onClick={saveEdit} className="w-full bg-saffron hover:bg-saffron-hover">
              {editBusy ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-ink">{label}</span>
      <span className="text-charcoal font-medium">{value}</span>
    </div>
  );
}
